<template>
  <div>
    <loader v-if="loading" :wrapperStyle="wrapperStyle" />

    <form-renderer
        id="create-group-form"
        class="mb10"
        title="articles.form.create.title"
        :model="model"
        :fields="['country']"
        :fieldsAdditions="countriesFieldsAdditions"
        :showSubmit="false"
        :validateFormTrigger="validateFormTrigger"
        @updateModel="updateModel"
    />

    <load-content
      v-if="model.country_id"
      :loading="loadingOutletsByCountry"
    >
      <form-renderer
        v-if="model.country_id"
        id="create-group-form-outlets"
        class="mb20"
        :showSubmit="false"
        :model="model"
        :fields="['outlets']"
        :fieldsAdditions="outletsFieldsAdditions"
        :validateFormTrigger="validateFormTrigger"
        @updateModel="updateModel"
      />
    </load-content>
    <form-renderer
        v-if="model.country_id && model.outlet_id"
        id="add-article-form"
        class="add-article-form "
        :showSubmit="false"
        :model="model"
        :fields="['date_published', 'language', 'priority', 'frontPage', 'press_release', 'url', 'headline', 'headline_language', 'headline_language', 'authors_id', 'content', 'should_sync_with_samsara']"
        :fieldsAdditions="fieldsAdditions"
        :validateFormTrigger="validateFormTrigger"
        @updateModel="updateModel"
    />

    <form-renderer
        v-if="model.country_id && model.outlet_id"
        ref="uploader"
        id="uploadImage"
        title="articles.form.files.title"
        :showSubmit="false"
        :fields="['files']"
        :fieldsAdditions="filesFieldsAdditions"
        @uploadComplete="uploadComplete"
    >
      <template v-slot:buttonRight>
        <div class="file-actions-wrapper">
          <input
              type="button"
              class="input-ui-sm btn btn-carma btn-save"
              :class="{disabled:saveDisabled}"
              value="Save Article"
              @click="saveArticle"/>
        </div>
      </template>
    </form-renderer>

  </div>
</template>

<script>
import Axios from 'axios';
import _ from 'lodash';
import { mapGetters, mapActions, mapState } from 'vuex';
import { API_HOST } from '../../config/app-const';
import { VALIDATIONS, MEDIA_SUBTYPES } from '../../config/const';
import Loader from '../loader';
import formRenderer from '../../components/form-renderer'
import LoadContent from '../load-content';

export default {
  name: "FormArticleCreate",

  props: {
    projectId: {
      type: [Number, String],
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    authors: Array,
  },

  components: {
    Loader,
    LoadContent,
    formRenderer,
  },

  data: function () {
    const datePublished = new Date();
    datePublished.setHours(0, 0, 0);

    return {
      loadingOutletsByCountry: false,
      model: {
        date_published: datePublished,
        country_id: null,
        outlet_id: null,
        language: null,
        priority: null,
        front_page: false,
        press_release: false,
        url: null,
        headline: null,
        headline_language: null,
        content: null,
        should_sync_with_samsara: true
      },
      article_id: null,
      countries: [],
      languages: [],
      loading: false,
      loadingOutlets: false,
      loadingForm: false,
      saveDisabled: false,
      baseURL: `${API_HOST}/analysis/${this.projectId}/articles/new`,
      wrapperStyle: {
        position: 'absolute',
        top: '50%',
        left: '50%'
      },
      outletsLoadingStyle: {
        position: 'relative'
      },
      validateFormTrigger: false,
      uploadTrigger: false,
      hasErrors: false,
      newArticle: {
        id: null
      }
    };
  },

  computed: {
    ...mapGetters({
      outletsOptionsToSelect: "admin/outlets/optionsToSelect"
    }),
    ...mapState({
      outlets: state => _.get(state.admin.outlets.outlets.data, 'data.data') || []
    }),
    config() {
      return {
        headers: {
          Authorization: `Token token=${this.user.accessToken}`
        }
      }
    },
    authorsOptions() {
      return this.authors.map(item => {
        return {
          value: item.id,
          label: `${item.first_name} ${item.last_name}`
        }
      })
    },
    languagesOptions() {
      return this.languages.map(item => {
        return {
          value: item.alpha_2_code,
          label: item.name
        }
      })
    },
    countriesFieldsAdditions() {
      return {
        country: {
          name: 'country_id',
          optionLabel: "name",
          optionValue: "id",
          translate_label: 'articles.labels.country.title',
          translatePlaceholder: 'outlets.country.select',
          style: "flex-base: 100%;",
          customClass: '',
          options: this.countries.length && this.countries || [],
        },
      }
    },
    outletsFieldsAdditions() {
      return {
        outlets: {
          name: 'outlet_id',
          optionLabel: "name",
          optionValue: "id",
          style: "flex-base: 100%;",
          customClass: '',
          options: this.outletsOptionsToSelect,
          loading: this.loadingOutlets,
          onSearchChange: (name) => {
            this.callSearchOutlet(name);
          }
        },
      }
    },
    fieldsAdditions() {
      const urlFieldAddition = {
        id: "article-url",
        label: "articles.labels.url.title",
        style: "flex-basis: 100%; order: 6;",
        validate: [VALIDATIONS.URL_FORMAT]
      }

      const selectedOutletId = this.model.outlet_id;
      const selectedMediaType = this.outlets.find(outlet => outlet.id === selectedOutletId).media_subtype;
      
      if ([MEDIA_SUBTYPES.WEBSITE, MEDIA_SUBTYPES.NEWS].indexOf(selectedMediaType) > -1) {
        urlFieldAddition.validate.push(VALIDATIONS.REQUIRED);
      }

      return {
        data_published: {
          style: "flex-basis: 33%; order: 1; align-self: baseline;",
        },
        language: {
          id: "article-language",
          style: "padding:0 1%; flex-basis: 32%; order: 2; align-self: baseline;",
          options: this.languagesOptions
        },
        priority: {
          id: "article-priority",
          style: "flex-basis: 33%; order: 3; align-self: baseline;",
        },
        frontPage: {
          style: "order: 4; flex-basis: 50%;",
        },
        press_release: {
          style: "order: 5; flex-basis: 50%;",
        },
        url: urlFieldAddition,
        headline: {
          style: "flex-basis: 100%; order: 7;",
        },
        headline_language: {
          style: "flex-basis: 49.5%; margin-right: 1%; order: 8;",
          options: this.languagesOptions
        },
        authors_id: {
          style: "flex-basis: 49.5%; order: 9;",
          options: this.authorsOptions
        },
        content: {
          style: "flex-basis: 100%; order: 10;",
        },
        should_sync_with_samsara: {
          style: "flex-basis: 100%; order: 11;",
        }
      }
    },

    getFilesUrl() { return this.newArticle.id ? `analysis/${this.projectId}/articles/assets/${this.newArticle.id}` : `analysis/${this.projectId}/articles/assets/null`},
    removeUrl() { return this.getFilesUrl && `${this.getFilesUrl}/delete` },
    uploadUrl() { return this.newArticleId ? `analysis/${this.projectId}/articles/assets/${this.newArticleId}/upload` : null},
    downloadUrl() { return this.getFilesUrl && `${this.getFilesUrl}/download` },

    filesFieldsAdditions() {
       return {
         files: {
           get_files_url: this.getFilesUrl,
           remove_url: this.removeUrl,
           upload_url: this.uploadUrl,
           download_url: this.downloadUrl,
           max_number_files: 20,
           max_file_size: "2mb",
           programmatic_upload: true,
           uploadTrigger: this.uploadTrigger,
           disableGetFiles: true,
           multiSelection: true,
           showLimit: true,
           hideSelected: true,
           appendUploadUrlWithUuid: false,
           prependFileName: this.prependFileName,
           loading: this.loadingOutlets
         }
       }
    },
    newArticleId() { return this.newArticle ? this.newArticle.id : null},
    prependFileName() { return this.newArticleId ? `uploads/articles/${this.newArticleId}/{uuid}-` : null}
  },

  mounted() {
    this.getCountries();
    this.getLanguages();
  },

  methods: {
    ...mapActions({
      getOutlets: "admin/outlets/getOutlets",
      clearOutlets: "admin/outlets/clearOutlets",
    }),

    getCountries() {
      this.loading = true;
      Axios.get(`${this.baseURL}/countries`, this.config)
        .then((response) =>  this.countries = response.data)
        .catch((error) =>  this.showError())
        .finally(() => this.loading = false)
    },
    getLanguages() {
      this.loadingForm = true;

      Axios.get(`${this.baseURL}/languages`, this.config)
        .then((response) => this.languages = response.data)
        .catch((error) => this.showError())
        .finally(() => this.loadingForm = false)
    },

    updateModel(newValue) {
      this.model = newValue;
    },

    triggerValidation() {
      this.validateFormTrigger = true
      this.$nextTick(() => {
        this.validateFormTrigger = false
        // find if any forms have errors
        const errors =  this.$children.map((child) => child.$children[0].validationMessages && child.$children[0].validationMessages.length
          ? child.$children[0] : null)
        .filter(form => form && form.validationMessages)
        this.hasErrors = errors.length > 0
      })
    },

    saveArticle() {
      this.triggerValidation()
      this.$nextTick(() => {
        if(!this.hasErrors) {
          this.createArticle()
        }
      })

    },
    createArticle() {
      this.loadingForm = true;

      let modelUrl = this.model.url;
      if (modelUrl) {
        modelUrl = modelUrl.trim();
        if (
          modelUrl.indexOf('https://') === -1 &&
          modelUrl.indexOf('http://') === -1
        ) {
          modelUrl = `http://${this.model.url}`;
        }
        this.model.url = modelUrl;
      }
      
      if(this.model.date_published){
        const utc = -(new Date().getTimezoneOffset() / 60);
        if(utc > 0)
          this.model.date_published.setDate(this.model.date_published.getDate() + 1);
        this.model.date_published.setHours(0, 0, 0);
      }

      Axios.post(`${this.baseURL}/save`, this.model, this.config)
        .then((response) => {
          this.newArticle = response.data.data
          this.$nextTick(() => this.triggerImageUpload())
        })
        .catch((error) => {
          this.saveDisabled = false;
          this.showError();
        })
        .finally(() => {
          this.loadingForm = false;
        });
    },
    triggerImageUpload() {
      this.uploadTrigger = true
      this.saveDisabled = true;
      this.$nextTick(() => this.uploadTrigger = false)
    },
    uploadComplete() {
      this.saveDisabled = false;
      this.$emit('on-article-created');
      this.model = {};

      this.$notify({
        group: 'store_notifications',
        type: 'success',
        text: this.$t('articles.form.create.success_message'),
      });
    },
    showError() {
      this.$notify({
        group: 'store_notifications',
        type: 'error',
        text: this.$t('messages.request.error'),
      });
    },
    callSearchOutlet(name = "") { 
      this.getOutlets({
        projectId: this.projectId,
        filterValues: {
          name: name,
          country_id: this.model.country_id,
        },
        pagination: {
          per_page: 20,
          page: 1,
          order: "name",
          sort: "asc",
        },
        forceKeep: true
      })
        .finally(() => {
          this.loadingOutletsByCountry = false;
        });
   },
  },
  watch: {
    'model.country_id': {
      handler() {
        this.clearOutlets();
        this.loadingOutletsByCountry = true;
        this.callSearchOutlet();
      }
    }
  }
};
</script>

<style lang="scss">
.add-article-form {

  .error-msgs {
    margin-bottom: 20px;
  }

  .form-group {
    flex: 1 1 100%;
    padding: 5px;
  }

  .input-ui,
  .multiselect__tags,
  .mx-input {
    background-color: $off-white;
    border-radius: 5px;
    border: none;
  }

  .mx-icon-calendar {
    top: 40%;
  }

  #article-content {
    min-height: 70px;
  }
}
</style>